import {
  fetchJobsCard,
  fetchJobTableAndChart,
  fetchJobByLocationVisual,
  fetchJobsByDepartmentVisual,
  fetchOpenJobChart,
  fetchJobDrilldown,
} from './actions';
import { getDefaultAtsFilters, buildReportsSlice } from '../common/ats-filters';
import { emptyValue, getTableData } from '../common/flat-transformer';
import { cardTransformer } from '../common/card-transformer';
import { visualPayload } from '../common/visual-transformer';
import { startCase } from 'lodash-es';
import { getPercentage } from '../common/get-percentage';

const getChartData = (response) => {
  const { table = [], headers, lookups: { dt, g2 } = {} } = response?.data;

  const xAxis = [...dt];

  const yAxis = g2.map((source, index) => ({
    data: Array(xAxis.length).fill(0),
    name:
      source?.name || (source ? startCase(source) : emptyValue?.appl_source),
    id: source || null,
  }));

  const valueIndex = headers.indexOf('opened_jobs');

  // Populate the data points
  table.forEach((weekRow) => {
    const weekIndex = weekRow[1]; // Week index (0, 1, 2, etc.)
    const sources = weekRow[0]; // Nested array of source data

    // Process each source entry for this week
    sources.forEach((sourceData) => {
      const g2Index = sourceData[3]; // g2 index (0, 1, 2, 3)
      const value = sourceData[valueIndex]; // opened_jobs value

      // If g2Index is valid and within our array bounds
      if (g2Index !== null && g2Index >= 0 && g2Index < yAxis.length) {
        yAxis[g2Index].data[weekIndex] = value;
      }
    });
  });

  return {
    xAxis,
    yAxis,
  };
};

const tableWithTotal = (data) => {
  const opened_jobs_total = data?.reduce(
    (acc, curr) => acc + curr?.opened_jobs,
    0
  );
  const closed_jobs_total = data?.reduce(
    (acc, curr) => acc + curr?.closed_jobs,
    0
  );
  const closed_without_hires_total = data?.reduce(
    (acc, curr) => acc + curr?.closed_jobs,
    0
  );
  const target_breached_jobs_total = data?.reduce(
    (acc, curr) => acc + curr?.target_breached_jobs,
    0
  );

  function innerFunc(data) {
    if (!data?.length) return [];
    return data.map((datum) => {
      return {
        ...datum,

        opened_jobs_total,
        opened_jobs_per: getPercentage(datum?.opened_jobs, opened_jobs_total),

        closed_jobs_total,
        closed_jobs_per: getPercentage(datum?.closed_jobs, closed_jobs_total),

        target_breached_jobs_total,
        target_breached_jobs_per: getPercentage(
          datum?.target_breached_jobs,
          target_breached_jobs_total
        ),

        closed_without_hires_total,
        closed_without_hires_per: getPercentage(
          datum?.closed_without_hires,
          closed_without_hires_total
        ),

        subRows: innerFunc(datum?.subRows),
      };
    });
  }

  return innerFunc(data);
};

const constructDrilldownData = (payload) => {
  const k1Map = payload?.data?.lookups?.k1;
  const k2Map = payload?.data?.lookups?.k2;
  const k3Map = payload?.data?.lookups?.k3;
  const k4Map = payload?.data?.lookups?.k4;
  const k5Map = payload?.data?.lookups?.k5;
  const k6Map = payload?.data?.lookups?.k6;
  const k7Map = payload?.data?.lookups?.k7;
  const k8Map = payload?.data?.lookups?.k8;
  const returnData = payload?.data?.table?.map((data) => {
    // eslint-disable-next-line no-unused-vars
    const [, k1, k2, k3, k4, k5, k6, k7, k8, time_to_close, workplace, work_type] =
      data;
    return {
      ...k1Map?.[k1],
      recruiters: k2Map?.[k2],
      hiring_managers: k3Map?.[k3],
      ...k4Map?.[k4],
      office_names: k5Map?.[k5],
      associated_requisitions: k6Map?.[k6],
      job_created_by: k7Map?.[k7],
      job_closed_by: k8Map?.[k8],
      time_to_close,
      workplace,
      work_type,
    };
  });

  return returnData;
};

const initialState = {
  ...getDefaultAtsFilters(),

  group: {
    group_by_1: null,
    group_by_2: null,
  },

  isCardLoading: false,
  cards: {},

  chart: {},
  table: [],
  isChartLoading: false,
  isTableLoading: false,

  jobsByDepartment: [],
  isJobsByDepartmentLoading: false,

  jobsByLocation: [],
  isjobsByLocationLoading: false,

  isDrillLoading: false,
  initialLoading: false,
  drilldownData: [],
  stageMapping: {},
  totalCount: 0,
  currentDrillPage: 1,
};

const extraReducers = {
  [fetchJobsCard.pending]: (state) => {
    state.isCardLoading = true;
  },
  [fetchJobsCard.fulfilled]: (state, { payload }) => {
    state.cards = cardTransformer(payload);
    state.isCardLoading = false;
  },
  [fetchJobsCard.rejected]: (state) => {
    state.isCardLoading = false;
  },

  [fetchJobTableAndChart.pending]: (state) => {
    state.isTableLoading = true;
  },
  [fetchJobTableAndChart.fulfilled]: (state, { meta, payload = {} }) => {
    state.isTableLoading = false;
    state.table = tableWithTotal(getTableData(payload, meta?.arg));
  },
  [fetchJobTableAndChart.rejected]: (state) => {
    state.isTableLoading = false;
  },

  [fetchOpenJobChart.pending]: (state) => {
    state.isChartLoading = true;
  },
  [fetchOpenJobChart.fulfilled]: (state, { payload = {} }) => {
    state.isChartLoading = false;
    state.chart = getChartData(payload);
  },
  [fetchOpenJobChart.rejected]: (state) => {
    state.isChartLoading = false;
  },

  [fetchJobByLocationVisual.pending]: (state) => {
    state.isjobsByLocationLoading = true;
  },

  [fetchJobByLocationVisual.fulfilled]: (state, { payload = {} }) => {
    state.jobsByLocation = visualPayload(payload);
    state.isjobsByLocationLoading = false;
  },

  [fetchJobByLocationVisual.rejected]: (state) => {
    state.isjobsByLocationLoading = false;
  },

  [fetchJobsByDepartmentVisual.pending]: (state) => {
    state.isJobsByDepartmentLoading = true;
  },
  [fetchJobsByDepartmentVisual.fulfilled]: (state, { payload }) => {
    state.jobsByDepartment = visualPayload(payload);
    state.isJobsByDepartmentLoading = false;
  },
  [fetchJobsByDepartmentVisual.rejected]: (state) => {
    state.isJobsByDepartmentLoading = false;
  },
  [fetchJobDrilldown.pending]: (state, { meta }) => {
    const { drilldown } = meta?.arg || {};
    if (drilldown.page === 1) {
      state.initialLoading = true;
    } else {
      state.isDrillLoading = true;
    }
  },
  [fetchJobDrilldown.fulfilled]: (state, { meta, payload = {} }) => {
    if (!payload) {
      return;
    }
    state.isDrillLoading = false;
    state.initialLoading = false;
    const { drilldown } = meta?.arg || {};
    const newData = constructDrilldownData(payload);

    state.totalCount = payload?.data?.pagination?.total_count;

    if (drilldown.page === 1) {
      state.drilldownData = newData;
    } else {
      state.drilldownData = [...state.drilldownData, ...newData];
    }
    state.currentDrillPage = drilldown.page;
  },
  [fetchJobDrilldown.rejected]: (state, action) => {
    if (
      action.error?.name === 'AbortError' ||
      action.error?.name === 'CanceledError'
    ) {
      return;
    }
    state.isDrillLoading = false;
    state.initialLoading = false;
  },
};

export const { reducer, actions } = buildReportsSlice({
  name: 'atsJobsReport',
  initialState,
  extraReducers,
});

export const {
  applyFilter,
  resetFilter,
  applyTimeFrame,
  applyViewBy,
  applyGroup,
  setFiltersData,
  applySearch,
  resetDrillDown,
} = actions;
export default reducer;
